// HEIC / HEIF display (owner 2026-11-01: "Heic, heif: that is client job"). Senders upload them as-is,
// and old data has some. Safari shows them natively; other browsers get a decoded JPEG blob URL.
// The decoder (heic2any, ~338 KB gzipped) is a lazy chunk, loaded only for a HEIC image on a browser
// that cannot show it. The browser's own support is probed once (a tiny HEIC in an Image()) and cached.
import logger from "./logger";

const HEIC_MIME = /^image\/hei[cf](-sequence)?$/i;
const HEIC_EXT = /\.hei[cf]$/i;

/** An 8x8 HEIC (heif-enc), the native-support probe. */
const PROBE_HEIC =
  "data:image/heic;base64,AAAAHGZ0eXBoZWljAAAAAG1pZjFoZWljbWlhZgAAAXxtZXRhAAAAAAAAACFoZGxyAAAAAAAAAABwaWN0AAAAAAAAAAAAAAAAAAAAACJpbG9jAAAAAERAAAEAAQAAAAABoAABAAAAAAAAACcAAAAjaWluZgAAAAAAAQAAABVpbmZlAgAAAAABAABodmMxAAAAAA5waXRtAAAAAAABAAAA/GlwcnAAAADcaXBjbwAAAHVodmNDAQNwAAAAkAAAAAAAHvAA/P34+AAADwNgAAEAGEABDAH//wNwAAADAJAAAAMAAAMAHroCQGEAAQApQgEBA3AAAAMAkAAAAwAAAwAeoCCBBZbqrprm4CGgwIAAAAyAAAADAIRiAAEABkQBwXPBiQAAABNjb2xybmNseAABAA0ABoAAAAAUaXNwZQAAAAAAAABAAAAAQAAAAChjbGFwAAAACAAAAAEAAAAIAAAAAf///8gAAAAC////yAAAAAIAAAAQcGl4aQAAAAADCAgIAAAAGGlwbWEAAAAAAAAAAQABBYECAwWEAAAAL21kYXQAAAAjKAGvBBITjOD46cJoP91R/FqVtJ7yftCrK+NJn3exGJKp/6A=";

const PROBE_TIMEOUT_MS = 3000;

/** True for a HEIC/HEIF image: by its mime when known, else by the URL's file extension. */
export function isHeic(src: string | null | undefined, mime?: string | null): boolean {
  if (mime) return HEIC_MIME.test(mime.trim());
  if (!src) return false;
  if (src.startsWith("data:")) return /^data:image\/hei[cf]/i.test(src);
  if (src.startsWith("blob:")) return false; // a local preview says nothing: the caller passes the File's type
  const path = src.split(/[?#]/, 1)[0];
  return HEIC_EXT.test(path);
}

let nativeProbe: Promise<boolean> | null = null;

/** Whether this browser shows HEIC in an <img> (probed once per page). */
export function supportsHeicNatively(): Promise<boolean> {
  if (nativeProbe) return nativeProbe;
  nativeProbe = new Promise<boolean>((resolve) => {
    try {
      const img = new Image();
      const timer = setTimeout(() => resolve(false), PROBE_TIMEOUT_MS);
      img.onload = () => {
        clearTimeout(timer);
        resolve(img.naturalWidth > 0);
      };
      img.onerror = () => {
        clearTimeout(timer);
        resolve(false);
      };
      img.src = PROBE_HEIC;
    } catch {
      resolve(false);
    }
  });
  return nativeProbe;
}

type Heic2Any = (opts: { blob: Blob; toType?: string; quality?: number }) => Promise<Blob | Blob[]>;
let decoder: Promise<Heic2Any> | null = null;
let decoderImports = 0;

function loadDecoder(): Promise<Heic2Any> {
  if (!decoder) {
    decoderImports++;
    decoder = import("heic2any").then((m) => m.default as Heic2Any);
    decoder.catch(() => {
      decoder = null; // a failed chunk load may succeed next time
    });
  }
  return decoder;
}

/** Fetches a HEIC image and decodes it to a JPEG blob URL. The caller revokes it. */
export async function decodeHeicToUrl(src: string): Promise<string> {
  const res = await fetch(src);
  if (!res.ok) throw new Error(`HEIC fetch failed: ${res.status}`);
  const blob = await res.blob();
  const heic2any = await loadDecoder();
  const out = await heic2any({ blob, toType: "image/jpeg", quality: 0.9 });
  const jpeg = Array.isArray(out) ? out[0] : out;
  if (!jpeg) throw new Error("HEIC decode gave no image");
  return URL.createObjectURL(jpeg);
}

// --- shared decodes ------------------------------------------------------------------------------
// One decode per HEIC URL, shared by every <img> showing it (a thumbnail and the full-size viewer).
// Each user holds a reference; at 0 the blob URL is kept GRACE_MS (reopening the viewer does not
// decode again), then revoked. At most MAX_DECODED entries: the least recently used unused one goes.

interface Decoded {
  /** The blob URL, or null when the decode failed (the caller then shows `src`, a broken image). */
  url: Promise<string | null>;
  refs: number;
  timer?: ReturnType<typeof setTimeout>;
}

export const MAX_DECODED = 50;
let graceMs = 30_000;
const decoded = new Map<string, Decoded>(); // insertion order = least recently used first

function drop(src: string, entry: Decoded): void {
  if (decoded.get(src) !== entry) return;
  clearTimeout(entry.timer);
  decoded.delete(src);
  void entry.url.then((u) => u && URL.revokeObjectURL(u));
}

function evictUnused(): void {
  for (const [src, entry] of decoded) {
    if (decoded.size <= MAX_DECODED) return;
    if (entry.refs === 0) drop(src, entry);
  }
}

/**
 * A reference to the decoded blob URL of `src` (decoded once, shared). Call `release` once when
 * done; the URL is revoked a grace period after the last release.
 */
export function acquireDecoded(src: string): { url: Promise<string | null>; release: () => void } {
  let entry = decoded.get(src);
  if (entry) {
    clearTimeout(entry.timer);
    entry.timer = undefined;
    decoded.delete(src); // re-insert: most recently used
  } else {
    entry = {
      refs: 0,
      url: decodeHeicToUrl(src).catch((err) => {
        logger.warn("[heicImage] HEIC decode failed", err);
        return null;
      }),
    };
  }
  entry.refs++;
  decoded.set(src, entry);
  evictUnused();
  const held = entry;
  let released = false;
  return {
    url: held.url,
    release: () => {
      if (released) return;
      released = true;
      held.refs--;
      if (held.refs > 0) return;
      held.timer = setTimeout(() => drop(src, held), graceMs);
    },
  };
}

/** Test hooks. */
export function resetHeicForTests(): void {
  nativeProbe = null;
  decoder = null;
  decoderImports = 0;
  graceMs = 30_000;
  for (const entry of decoded.values()) clearTimeout(entry.timer);
  decoded.clear();
}
export function setHeicGraceForTests(ms: number): void {
  graceMs = ms;
}
/** How many times the decoder chunk was imported since the last reset. */
export function heicDecoderImportsForTests(): number {
  return decoderImports;
}
/** How many decodes are cached (in use or in their grace period). */
export function heicDecodedCountForTests(): number {
  return decoded.size;
}
